import { Tab } from "@headlessui/react";
import {
    CheckIcon,
    ClipboardDocumentIcon,
    KeyIcon,
    LockClosedIcon,
} from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { createColumnHelper } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import type { DataImport, OAuthApplication } from "../../apiClient/generated";
import { DataTableV3 } from "../../components/DataTable/DataTableV3";
import {
    useAccountsApiClient,
    useAppSelector,
    useDataImportApiClient,
} from "../../hooks";
import { Card } from "../../ui/Card";
import { formatDate } from "../../utils/dates";
import { ROUTES } from "..";

const columnHelper = createColumnHelper<DataImport>();
const columns = [
    columnHelper.accessor("createdAt", {
        id: "createdAt",
        cell: (info) => formatDate(info.getValue()),
        header: () => "Created At",
        enableSorting: true,
    }),
    columnHelper.accessor("importType", {
        id: "importType",
        header: () => "Import Type",
        cell: (info) => info.renderValue(),
        enableSorting: true,
    }),
    columnHelper.accessor("status", {
        id: "status",
        cell: (info) => info.getValue(),
        header: () => "Status",
        enableSorting: true,
    }),
    columnHelper.accessor("importParams", {
        id: "importParams",
        header: () => "Import Params",
        cell: (info) => JSON.stringify(info.renderValue()),
    }),
];

const CopyButton: React.FC<{ value: string }> = ({ value }) => {
    const [copied, setCopied] = useState(false);
    const copy = useCallback(() => {
        navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [value]);
    return (
        <button
            className="ml-2 p-1 rounded hover:bg-slate-700 hover:text-white"
            onClick={copy}
            title="Copy to clipboard"
        >
            {copied ? (
                <CheckIcon className="w-4 h-4 text-green-400" />
            ) : (
                <ClipboardDocumentIcon className="w-4 h-4" />
            )}
        </button>
    );
};

const ManageAPIKeyCard: React.FC = () => {
    const accountsApiClient = useAccountsApiClient();

    // --- Legacy API key state ---
    const [token, setToken] = useState<string | null>(null);

    useEffect(() => {
        accountsApiClient
            .accountsTokenRetrieve()
            .then((response) => setToken(response.token))
            .catch(() => setToken(null));
    }, [accountsApiClient]);

    const regenerateToken = useCallback(() => {
        accountsApiClient
            .accountsTokenCreate()
            .then((response) => setToken(response.token))
            .catch(() => setToken(null));
    }, [accountsApiClient]);

    // --- OAuth credentials state ---
    const [oauthApp, setOauthApp] = useState<
        OAuthApplication | null | undefined
    >(undefined);
    const [newSecret, setNewSecret] = useState<string | null>(null);
    const [appName, setAppName] = useState("");
    const [oauthLoading, setOauthLoading] = useState(false);

    useEffect(() => {
        accountsApiClient
            .accountsOauthCredentialsRetrieve()
            .then((app) => setOauthApp(app))
            .catch(() => setOauthApp(null));
    }, [accountsApiClient]);

    const createCredentials = useCallback(() => {
        if (!appName.trim()) return;
        setOauthLoading(true);
        accountsApiClient
            .accountsOauthCredentialsCreate({
                oAuthApplicationCreateRequest: { name: appName.trim() },
            })
            .then((result) => {
                setOauthApp({
                    name: result.name,
                    clientId: result.clientId,
                    created: result.created,
                });
                setNewSecret(result.clientSecret);
                setAppName("");
            })
            .finally(() => setOauthLoading(false));
    }, [accountsApiClient, appName]);

    const regenerateSecret = useCallback(() => {
        setOauthLoading(true);
        accountsApiClient
            .accountsOauthCredentialsRegenerateSecretCreate()
            .then((result) => {
                setNewSecret(result.clientSecret);
            })
            .finally(() => setOauthLoading(false));
    }, [accountsApiClient]);

    const tabClass = ({ selected }: { selected: boolean }) =>
        `flex items-center py-1 px-2 rounded-lg ${
            selected ? "font-bold" : "hover:text-slate-100 hover:bg-slate-800"
        }`;

    return (
        <Card>
            <Tab.Group>
                <div className="flex justify-between py-4 px-10 items-center">
                    <h1 className="text-xl">API Keys</h1>
                    <Tab.List className="flex space-x-4">
                        <Tab className={tabClass}>
                            <LockClosedIcon className="w-5 h-5 mr-1" />
                            API Key
                        </Tab>
                        <Tab className={tabClass}>
                            <KeyIcon className="w-5 h-5 mr-1" />
                            OAuth Credentials
                        </Tab>
                    </Tab.List>
                </div>
                <hr />

                <Tab.Panels>
                    {/* --- Legacy API Key tab --- */}
                    <Tab.Panel>
                        <div className="px-10 py-6">
                            <h2 className="font-bold text-lg">API Key</h2>
                            <p className="mt-2">
                                You can use this page to retrieve API keys to
                                use in data pipelines.
                            </p>
                            <p className="mt-2">
                                Regenerating the API key revokes previous keys.
                            </p>
                            <div className="flex mt-2">
                                <input
                                    type="text"
                                    readOnly={true}
                                    placeholder="No API key"
                                    className="rounded-lg w-96"
                                    value={token || ""}
                                />
                                <button
                                    className="ml-3 px-4 rounded-lg italic hover:bg-slate-700 hover:text-white"
                                    onClick={regenerateToken}
                                >
                                    Regenerate
                                </button>
                            </div>
                        </div>
                    </Tab.Panel>

                    {/* --- OAuth Credentials tab --- */}
                    <Tab.Panel>
                        <div className="px-10 py-6">
                            <h2 className="font-bold text-lg">
                                OAuth Credentials
                            </h2>
                            <p className="mt-2">
                                Use these credentials with the{" "}
                                <code>client_credentials</code> OAuth flow in
                                data pipeline scripts.
                            </p>

                            {oauthApp === undefined && (
                                <p className="mt-4 text-slate-400">Loading…</p>
                            )}

                            {oauthApp === null && (
                                <div className="mt-4">
                                    <p className="mb-2">
                                        No credentials yet. Enter an application
                                        name to create them.
                                    </p>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            placeholder="Application name"
                                            className="rounded-lg w-72"
                                            value={appName}
                                            onChange={(e) =>
                                                setAppName(e.target.value)
                                            }
                                            onKeyDown={(e) =>
                                                e.key === "Enter" &&
                                                createCredentials()
                                            }
                                        />
                                        <button
                                            className="px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-600 disabled:opacity-50"
                                            onClick={createCredentials}
                                            disabled={
                                                oauthLoading || !appName.trim()
                                            }
                                        >
                                            Create
                                        </button>
                                    </div>
                                </div>
                            )}

                            {oauthApp !== null && oauthApp !== undefined && (
                                <div className="mt-4 space-y-3">
                                    <div>
                                        <span className="text-sm text-slate-400">
                                            Application
                                        </span>
                                        <div className="font-mono mt-0.5">
                                            {oauthApp.name}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-sm text-slate-400">
                                            Client ID
                                        </span>
                                        <div className="flex items-center font-mono mt-0.5">
                                            <span>{oauthApp.clientId}</span>
                                            <CopyButton
                                                value={oauthApp.clientId}
                                            />
                                        </div>
                                    </div>

                                    {newSecret ? (
                                        <div className="rounded-lg border border-yellow-500 bg-yellow-500/10 p-4">
                                            <p className="text-sm font-semibold text-yellow-400 mb-2">
                                                Copy your client secret now — it
                                                will not be shown again.
                                            </p>
                                            <div className="flex items-center font-mono break-all">
                                                <span>{newSecret}</span>
                                                <CopyButton value={newSecret} />
                                            </div>
                                            <button
                                                className="mt-3 text-sm underline text-slate-400 hover:text-white"
                                                onClick={() =>
                                                    setNewSecret(null)
                                                }
                                            >
                                                I've saved it, dismiss
                                            </button>
                                        </div>
                                    ) : (
                                        <div>
                                            <span className="text-sm text-slate-400">
                                                Client Secret
                                            </span>
                                            <div className="font-mono mt-0.5 text-slate-400">
                                                ••••••••••••••••
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        className="px-4 py-2 rounded-lg border border-slate-600 hover:bg-slate-700 hover:text-white disabled:opacity-50"
                                        onClick={regenerateSecret}
                                        disabled={oauthLoading}
                                    >
                                        Regenerate Secret
                                    </button>
                                </div>
                            )}
                        </div>
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
        </Card>
    );
};

const DataImportTableCard: React.FC = () => {
    const apiClient = useDataImportApiClient();
    const [filterSearch, setFilterSearch] = useState("");
    const debouncedSearch = useDebounce(filterSearch, 400);

    const fetchFunction = async (props: any) => {
        return await apiClient.dataImportList(props);
    };

    return (
        <Card>
            <div className="mx-4 mb-3 flex items-center text-sm justify-between">
                <div className="flex items-center">
                    <input
                        type="text"
                        className="rounded-lg py-1 px-2"
                        placeholder="Search by CSV filename"
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                    />
                    <MagnifyingGlassIcon className="w-5 h-5 -ml-7" />
                </div>
            </div>
            <hr />
            <DataTableV3
                dataName="dataImport"
                columns={columns}
                extraFilters={{
                    search: debouncedSearch,
                }}
                fetchFunction={fetchFunction}
                sortable={true}
                filterable={false}
            />
        </Card>
    );
};

export const AdminDataImportPage = () => {
    const isStaff = useAppSelector((state) => state.auth.isStaff);

    if (!isStaff) {
        return <Navigate to={ROUTES.ROOT} />;
    }

    return (
        <div className="container mx-auto px-6">
            <h1 className="mt-6 text-2xl">Manage Data Imports</h1>
            <ManageAPIKeyCard />
            <DataImportTableCard />
        </div>
    );
};
